<script setup>
import { ref, watch, computed, defineProps, defineEmits } from 'vue'
import DateField from '@/components/panels/DateField.vue'

// Universal new-design form modal (.modal--form).
// fields: [{ key, label, placeholder, type, options, required, full, dependsOn,
//            readonly, optional, accept, rows, placeholderDisabled, autocomplete }]
//   optional on a select makes its empty placeholder option pickable (clears the value).
//   type: 'text' | 'email' | 'number' | 'password' | 'select' | 'textarea' | 'date' | 'file'
//   autocomplete: e.g. 'new-password' keeps the browser from filling in a saved login.
//   options: an array (of scalars or {value, label}) or a function (vals) => array.
//   dependsOn: parent field key — the field is disabled while the parent is empty,
//   and clears when the parent changes.
// Emits `change(key, value)` on every edit, so a page can lazy-load a dependent
// field's options (e.g. addresses of the picked city).
const props = defineProps({
  open: { type: Boolean, default: false },
  title: { type: String, required: true },
  fields: { type: Array, required: true },
  initial: { type: Object, default: () => ({}) },
  submitLabel: { type: String, default: 'Save' },
  submitting: { type: Boolean, default: false },
})

const emit = defineEmits(['submit', 'cancel', 'change'])

const vals = ref({})

watch(() => props.open, (isOpen) => {
  if (isOpen) vals.value = { ...props.initial }
}, { immediate: true })

function set(key, value) {
  vals.value[key] = value
  props.fields.forEach((f) => {
    if (f.dependsOn === key) vals.value[f.key] = ''
  })
  emit('change', key, value)
}

function optionsOf(field) {
  const raw = typeof field.options === 'function' ? field.options(vals.value) : (field.options || [])
  return raw.map((o) => (o && typeof o === 'object' ? o : { value: o, label: String(o) }))
}

function isDisabled(field) {
  return !!(field.dependsOn && !vals.value[field.dependsOn])
}

const missing = computed(() =>
  props.fields.some((f) => {
    if (!f.required || f.readonly) return false
    const v = vals.value[f.key]
    if (f.type === 'file') return !v
    return !String(v ?? '').trim()
  })
)

function onFile(field, e) {
  const file = e.target.files && e.target.files[0]
  set(field.key, file || '')
}

function submit() {
  if (missing.value || props.submitting) return
  emit('submit', { ...vals.value })
}
</script>

<template>
  <Teleport to="body">
    <div v-if="open" class="modal-overlay" @click.self="emit('cancel')">
      <div class="modal modal--form">
        <div class="modal__formhead">
          <h3 class="modal__title">{{ title }}</h3>
          <button class="modal__close" type="button" @click="emit('cancel')">
            <span class="mdi mdi-close"></span>
          </button>
        </div>
        <div class="formgrid">
          <label
            v-for="field in fields"
            :key="field.key"
            class="field"
            :class="{ 'field--full': field.full, 'field--disabled': isDisabled(field) }">
            <span class="field__label">
              {{ field.label }}
              <i v-if="field.required && !field.readonly"> *</i>
              <span v-if="field.readonly" class="roflag">
                <span class="mdi mdi-lock-outline"></span> read-only
              </span>
              <em v-if="field.optional" class="field__opt"> optional</em>
            </span>

            <div v-if="field.readonly" class="field__input field__ro">
              <template v-if="vals[field.key]">{{ vals[field.key] }}</template>
              <span v-else class="field__romuted">{{ field.placeholder || 'Set automatically' }}</span>
            </div>

            <div v-else-if="field.type === 'select'" class="field-select field-select--block">
              <select
                :value="vals[field.key] ?? ''"
                :disabled="isDisabled(field)"
                @change="set(field.key, $event.target.value)">
                <option value="" :disabled="!field.optional">
                  {{ isDisabled(field) ? (field.placeholderDisabled || 'Select previous field first…') : (field.placeholder || 'Select…') }}
                </option>
                <option
                  v-for="o in optionsOf(field)"
                  :key="o.value"
                  :value="o.value"
                  :selected="String(o.value) === String(vals[field.key] ?? '')">{{ o.label }}</option>
              </select>
              <span class="mdi mdi-chevron-down caret"></span>
            </div>

            <textarea
              v-else-if="field.type === 'textarea'"
              class="field__input field__textarea"
              :rows="field.rows || 3"
              :placeholder="field.placeholder || ''"
              :value="vals[field.key] ?? ''"
              @input="set(field.key, $event.target.value)"></textarea>

            <span v-else-if="field.type === 'file'" class="filepick">
              <input type="file" :accept="field.accept || ''" style="display: none" @change="onFile(field, $event)">
              <span class="mdi mdi-tray-arrow-up"></span>
              <span class="filepick__t">{{ vals[field.key]?.name || field.placeholder || 'Choose a file…' }}</span>
            </span>

            <DateField
              v-else-if="field.type === 'date'"
              :model-value="vals[field.key] ?? ''"
              :placeholder="field.placeholder"
              @update:model-value="set(field.key, $event)" />

            <input
              v-else
              class="field__input"
              :type="field.type || 'text'"
              :autocomplete="field.autocomplete || null"
              :placeholder="field.placeholder || ''"
              :value="vals[field.key] ?? ''"
              @input="set(field.key, $event.target.value)">
          </label>
        </div>
        <div class="modal__actions modal__actions--right">
          <button class="btn-ghost" type="button" @click="emit('cancel')">Cancel</button>
          <button class="btn-primary" type="button" :disabled="missing || submitting" @click="submit">
            <span v-if="submitting" class="mdi mdi-loading mdi-spin"></span>
            {{ submitLabel }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>
